import {
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Popover,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueries } from 'react-query';
import { NumericFormat } from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTextField from 'components/ui/form/FormTextField';
import FormSelectField from 'components/ui/form/FormSelectField';
import FormDateField from 'components/ui/form/FormDateField';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';

// for react window //
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import ProductRowRWindow from './ProductRowRWindow';
import moment from 'moment/moment';
// for react window //

const itemKey = (index, data) => data?.items?.[index]?.id;

const validationSchema = yup.object({
  supplier_id: yup.string().required('Yetkazib beruvchini tanlash majburiy!'),
  comment: yup.string().required('Izoh kiritish majburiy!'),
  date: yup
    .date()
    .typeError("Sana noto'g'ri kiritilgan!")
    .required('Sana kiritish majburiy!'),
});

const WarehouseInbound = () => {
  const { setComponent } = useTopPanel();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('');
  const { submit, isSubmitting } = useFormSubmit();
  const { id } = useParams();

  const [valuta, setValuta] = useState('');
  const [category, setCategory] = useState('');
  const [conversation, setConversation] = useState({ uzs: false });
  const [selectedValute, setSelectedValute] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const warehouse = JSON.parse(localStorage.getItem('warehouse-item-data'));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const idPopover = open ? 'simple-popover' : undefined;

  useEffect(() => {
    setComponent(
      <div className='component-title'>
        {warehouse?.name ? `${warehouse?.name}dan` : 'Ombordan'}&nbsp;
        mahsulotlarni qaytarish
      </div>
    );
  }, []);

  const formik = useFormik({
    initialValues: {
      comment: '',
      supplier_id: '',
      date: moment().format('YYYY-MM-DD'),
    },
    validationSchema,
    onSubmit: async (values) => {
      const newValues = {
        ...values,
        is_conversion: conversation.uzs ? 1 : 0,
        conversion_rate: valuta || 0,
        converted_amount: uzsToUsdReduce || 0,
        products: products
          .filter((pr) => pr?.amount)
          .map(({ product_id, amount, isvalute, price }) => ({
            product_id,
            amount,
            isvalute,
            price,
          })),
      };
      const url = `/admin/warehouses/returnpro/${id}`;
      const message = 'Maxsulot qaytarildi!';
      const type = 'post';

      submit(
        { type, contentType: 'simple' },
        newValues,
        url,
        message,
        id,
        true,
        handleBack
      );
    },
  });

  const handleBack = () => {
    navigate(`/admin/warehouse/${id}/return-list`);
    formik.resetForm();
  };

  const [{ isLoading, isFetching }, { data }, { data: categories }] =
    useQueries([
      {
        queryKey: ['/admin/warehouses/show', id],
        queryFn: async () => {
          const res = await axiosPrivate.get(`/admin/warehouses/show/${id}`);
          return res.data.data;
        },
        enabled: !hasError && !!id,
        onSuccess: (data) => {
          setProducts(
            data?.map((pr) => ({
              ...pr,
              name: pr?.product?.name,
              unit: pr?.product?.unit?.name,
              category: pr?.product?.category?.name,
              category_id: pr?.product?.category_id,
              amount: '',
              max_amount: Number(pr?.amount) || 0,
            }))
          );
        },
        onError: () => setHasError(true),
        retry: false,
      },
      {
        queryKey: '/dictionary/valutes',
        queryFn: async function () {
          const response = await axiosPrivate.get('/dictionary/valutes');
          return response.data.data;
        },

        enabled: !hasError,
        onError: (error) => {
          setHasError(true);
          // setBlocksError(true);
        },
        retry: false,
      },
      {
        queryKey: '/dictionary/product-category',
        queryFn: async function () {
          const response = await axiosPrivate.get(
            '/dictionary/product-category'
          );
          return response.data.data;
        },

        enabled: !hasError,
        onError: (error) => {
          setHasError(true);
          // setBlocksError(true);
        },
        retry: false,
      },
    ]);

  const setValue = useCallback((id, field, value) => {
    if (id == 'all') {
      setProducts((prev) => prev.map((pr) => ({ ...pr, [field]: value })));
    } else
      setProducts((prev) =>
        prev?.map((pr) => (pr?.id == id ? { ...pr, [field]: value } : pr))
      );
  }, []);

  const filteredData = useMemo(() => {
    if (!products?.length) return [];

    let result = products;
    const term = filter?.toUpperCase();

    if (category) {
      result = result.filter((p) => p?.category_id == category);
    }

    if (filter) {
      result = result.filter((p) => p?.name?.toUpperCase()?.includes(term));
    }

    return result;
  }, [products, filter, category]);

  const sumReduce = useMemo(() => {
    if (!products.length) return 0;

    return products
      ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 0)
      ?.reduce((a, c) => a + c.amount * c.price, 0);
  }, [products]);

  const uzsToUsdReduce = useMemo(() => {
    if (!products.length || !valuta || !conversation.uzs) return 0;

    return products
      ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 0)
      ?.reduce((a, c) => a + (c.amount * c.price) / valuta, 0);
  }, [products, valuta, conversation.uzs]);

  const valutaReduce = useMemo(() => {
    if (!products.length) return 0;

    if (conversation.usd && valuta)
      return products
        ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 1)
        ?.reduce((a, c) => a + (c?.amount * c?.price) / valuta, 0);
    let result = products
      ?.filter((pr) => pr?.amount && pr?.price && pr?.isvalute == 1)
      ?.reduce((a, c) => a + c?.amount * c?.price, 0);

    return result;
  }, [products, valuta, conversation.usd]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-table-wrapper h-[calc(100vh-80px)]'>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={1.5} sm={1.5}>
              <FormDateField label='Sana' fieldName='date' formik={formik} />
            </Grid>
            <Grid item xs={2} sm={2}>
              <FormSelectField
                label='Yetkazib beruvchi'
                fieldName='supplier_id'
                formik={formik}
                path='/dictionary/suppliers'
              />
            </Grid>
            <Grid item xs={6.5} sm={6.5}>
              <FormTextField label='Izoh' fieldName='comment' formik={formik} />
            </Grid>
            <Grid item xs={2} sm={2}>
              <div className='flex items-center gap-4'>
                <div className='[&>div]:flex [&>div]:whitespace-nowrap [&>div]:mt-2'>
                  <FormActionButtons
                    isSubmitting={isSubmitting}
                    disabled={!products.some((pr) => pr.amount > 0)}
                    formType='dialog'
                    setOpen={handleClose}
                    reset={formik.resetForm}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </form>

        <div className='component-list-wrapper'>
          <div className='component-table-wrapper'>
            <div className='calls-history-tab-item'>
              <TableContainer
                className='tab-table-container !shadow-none !base-border h-[calc(100vh-140px)]'
                component='div'
              >
                <Table stickyHeader component='div'>
                  <TableHead component='div'>
                    <Grid
                      container
                      component='div'
                      columns={{ xs: 12, sm: 12, lg: 12 }}
                      className={
                        'no-underline table-fixed pr-[15px] bg-gray-50 border-y border-t-transparent items-center'
                      }
                    >
                      <Grid item xs={0.5} className='!bg-gray-50'>
                        <div className='font-semibold px-2 py-2'>#</div>
                      </Grid>
                      <Grid item xs={2.5} className='!bg-gray-50'>
                        <div className='font-semibold flex items-center gap-2 py-2 px-2'>
                          <span>Nomi</span>
                          <TextField
                            className='!m-0'
                            size='small'
                            placeholder='Qidiruv'
                            value={filter}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position='end'
                                  className='custom-endAdornment'
                                >
                                  {filter ? (
                                    <IconButton
                                      onClick={() => setFilter('')}
                                      variant='onlyIcon'
                                    >
                                      <i className='bi bi-x end-adornment-close-button' />
                                    </IconButton>
                                  ) : (
                                    <IconButton
                                      variant='onlyIcon'
                                      disabled
                                      disableRipple
                                    >
                                      <i className='bi bi-search text-base leading-4' />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                            onChange={(e) => setFilter(e?.target?.value)}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={2}>
                        <div className='flex w-full items-center gap-2'>
                          <span>Kategoriyasi</span>
                          <div className='w-full max-w-[calc(100%-98px)]'>
                            <Select
                              fullWidth
                              onChange={(_, val) => {
                                setCategory(val?.props?.value);
                              }}
                              value={category}
                              size='small'
                              // className='max-w-[171px]'
                              endAdornment={
                                <InputAdornment
                                  position='end'
                                  className='custom-endAdornment'
                                >
                                  {category && (
                                    <IconButton
                                      onClick={() => {
                                        setValue('all', 'isvalute', null);
                                        setCategory(null);
                                      }}
                                      variant='onlyIcon'
                                      className='select-box-end-adornment-button'
                                    >
                                      <i className='bi bi-x end-adornment-close-button' />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              }
                            >
                              {categories?.map((opt) => (
                                <MenuItem value={opt?.id} key={opt?.id}>
                                  {opt?.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </Grid>
                      <Grid item xs={1}>
                        <div className='font-semibold px-2 py-2'>Birlik</div>
                      </Grid>
                      <Grid item xs={1.5}>
                        <div className='font-semibold px-2 py-2'>Miqdori</div>
                      </Grid>
                      <Grid item xs={2.5}>
                        <div className='flex items-center gap-2 font-semibold px-2 py-2'>
                          Birlik narxi
                          <IconButton
                            aria-describedby={idPopover}
                            variant='contained'
                            size='small'
                            onClick={handleClick}
                          >
                            <i className='bi bi-three-dots-vertical' />
                          </IconButton>
                          <Popover
                            id={idPopover}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            PaperProps={{
                              className: 'p-6 !base-border !shadow-md',
                            }}
                          >
                            <div>
                              <label className='flex justify-between items-center mb-4 border border-gray-400 cursor-pointer pl-3 text-sm rounded-lg'>
                                <span className='mr-2 font-semibold select-none'>
                                  UZSni konvertatsiya
                                </span>
                                <Switch
                                  checked={conversation.uzs}
                                  onChange={(e) =>
                                    setConversation((prev) => ({
                                      ...prev,
                                      uzs: e.target.checked,
                                    }))
                                  }
                                />
                              </label>
                              <NumericFormat
                                label='Valyuta kursi'
                                onChange={(event) => {
                                  const formattedValue =
                                    event.target.value &&
                                    parseFloat(
                                      event.target.value?.replaceAll(' ', '')
                                    );

                                  setValuta(formattedValue);
                                }}
                                fullWidth
                                value={valuta}
                                customInput={TextField}
                                allowNegative={false}
                                thousandSeparator={' '}
                                sx={{ m: 0, minWidth: 180 }}
                                decimalScale={3}
                              />
                            </div>
                          </Popover>
                          <span className='text-green-500 flex flex-wrap whitespace-nowrap gap-3'>
                            <CurrencyFormat value={sumReduce} suffix=' UZS' />
                            <CurrencyFormat value={valutaReduce} />
                            <br></br>
                            {conversation.uzs && valuta && (
                              <CurrencyFormat
                                value={uzsToUsdReduce}
                                suffix=' UZS → USD '
                              />
                            )}
                          </span>
                        </div>
                      </Grid>
                      <Grid item xs={2}>
                        <div className='flex items-center gap-2'>
                          <span>Valyuta</span>
                          <Select
                            fullWidth
                            onChange={(_, val) => {
                              setValue('all', 'isvalute', val?.props?.value);
                              setSelectedValute(val?.props?.value);
                            }}
                            value={selectedValute}
                            size='small'
                            className='max-w-[calc(100%-60px)]'
                            endAdornment={
                              <InputAdornment
                                position='end'
                                className='custom-endAdornment'
                              >
                                {selectedValute !== null && (
                                  <IconButton
                                    onClick={() => {
                                      setValue('all', 'isvalute', null);
                                      setSelectedValute(null);
                                    }}
                                    variant='onlyIcon'
                                    className='select-box-end-adornment-button'
                                  >
                                    <i className='bi bi-x end-adornment-close-button' />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            }
                          >
                            {data?.map((opt) => (
                              <MenuItem value={opt?.id} key={opt?.id}>
                                {opt?.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </Grid>
                    </Grid>
                  </TableHead>
                  {isLoading || isFetching ? (
                    <TableBody className='overflow-hidden' component='div'>
                      <TableRow component='div'>
                        <TableCell colSpan={7}>
                          <LinearProgress />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : filteredData && filteredData?.length > 0 ? (
                    <TableBody
                      className='overflow-hidden h-[calc(100vh-200px)] w-full'
                      component='div'
                    >
                      <AutoSizer>
                        {({ height, width }) => (
                          <List
                            height={height}
                            width={width}
                            itemCount={filteredData.length}
                            itemSize={65}
                            itemKey={itemKey}
                            itemData={{
                              conversation,
                              valuta,
                              selectedValute,
                              listOfValuta: data,
                              items: filteredData,
                              onChange: setValue,
                            }}
                          >
                            {ProductRowRWindow}
                          </List>
                        )}
                      </AutoSizer>
                    </TableBody>
                  ) : (
                    <TableBody component='div'>
                      <TableRow component='div'>
                        <TableCell colSpan={7}>
                          <span className='text-gray-400 flex items-center justify-center'>
                            <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                            Ma'lumot topilmadi!
                          </span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(WarehouseInbound);
